import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr'

const baseUrl = import.meta.env.VITE_API_URL || 'https://localhost:7040'

class SignalRService {
  private connection: HubConnection | null = null
  private hubPath: string | null = null

  startConnection(hubPath: string, initFn?: () => void) {
    // If already connected to the same hub, don't create a new connection
    if (
      this.connection?.state === HubConnectionState.Connected &&
      this.hubPath === hubPath
    ) {
      console.log('SignalR connection already established to this hub.')
      // Still call the init function if provided
      initFn?.()
      return
    }

    // If already connected, stop the current connection first
    if (this.connection?.state === HubConnectionState.Connected) {
      console.log(
        'Stopping existing SignalR connection before connecting to new hub.'
      )
      this.connection.stop()
    }

    this.connection = new HubConnectionBuilder()
      .withUrl(baseUrl + hubPath, {
        // Ensure cookies are sent with the SignalR connection
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .build()

    this.hubPath = hubPath

    this.connection
      .start()
      .then(() => {
        console.log(`SignalR connection established to hub: ${hubPath}`)
        initFn?.()
      })
      .catch((err) => {
        console.error('Error starting SignalR connection:', err)
        this.hubPath = null
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onReceiveMessage(method: string, callback: (...args: any[]) => any) {
    if (!this.connection) {
      console.warn('Receive: SignalR connection is not established.')
      return
    }
    this.connection.on(method, callback)
  }

  sendMessage(method: string, ...messages: unknown[]) {
    if (this.connection?.state !== HubConnectionState.Connected) {
      console.warn('Send: SignalR connection is not established.')
      return
    }
    console.log('Invoke: ' + method + ' for messages sent:', ...messages)
    this.connection.invoke(method, ...messages).catch((err) => {
      console.error('Error sending message:', err)
    })
  }

  stop() {
    if (this.connection?.state !== HubConnectionState.Connected) {
      console.log('Stop: SignalR connection is not established.')
      return
    }

    return this.connection.stop().then(() => {
      console.log('SignalR connection stopped.')
      this.hubPath = null
    })
  }
}

export abstract class HubService {
  protected hub: SignalRService

  constructor() {
    this.hub = new SignalRService()
  }

  abstract startHubConnection(): void

  stopHubConnection() {
    return this.hub.stop()
  }
}

// Create a default instance for backward compatibility if needed
export const defaultSignalRService = new SignalRService()
export default defaultSignalRService
